import { useQuery } from '@tanstack/react-query'
import { Link, useLocation } from '@tanstack/react-router'
import { History, Mail, MessageSquareText, Share2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useQuoteBuilderApi } from '../api/client'
import { quoteQueryOptions, workflowQueryOptions } from '../api/queries'
import { waitingCount } from '../comments/api'
import { openActionCount, quoteEmailsQueryOptions } from '../emails/api'
import { quoteCommentsQueryOptions } from '../publishing/api'
import { ProjectLinkAction } from '../project/project-link-action'
import { PublishQuoteAction } from './publish-action'
import { usePageTitle } from '@/lib/page-title'

/**
 * The current quote, pinned above every quote page (the demo shows it under its top bar):
 * number and name, stage progress, version history, customer share and publish.
 */
export function QuoteContextBar({ quoteId }: { quoteId: string }) {
  const api = useQuoteBuilderApi()
  const { pathname } = useLocation()
  const onWorkflow = pathname.includes('/workflow/')
  const quoteQuery = useQuery(quoteQueryOptions(api, quoteId))
  const workflowQuery = useQuery({
    ...workflowQueryOptions(api, quoteId),
    enabled: onWorkflow,
  })
  const emailsQuery = useQuery(quoteEmailsQueryOptions(api, quoteId))
  const commentsQuery = useQuery(quoteCommentsQueryOptions(api, quoteId))
  const quote = quoteQuery.data
  usePageTitle(quote && `Quote ${quote.quote_number}`)
  const workflow = workflowQuery.data
  const actions = openActionCount(emailsQuery.data)
  const unanswered = waitingCount(commentsQuery.data)

  return (
    <div className="sticky -top-4 z-20 -mx-4 -mt-4 mb-4 flex min-h-[52px] flex-wrap items-center gap-x-3 gap-y-1 border-b bg-background px-4 py-2 md:-top-6 md:-mx-6 md:-mt-6 md:mb-6 md:px-6">
      <span className="hidden shrink-0 text-xs font-medium text-muted-foreground sm:inline">Current quote</span>
      <span className="min-w-0 flex-1 truncate text-sm font-semibold">
        {quote ? (
          <>
            <span className="tabular-nums">{quote.quote_number}</span>
            <span className="font-normal text-muted-foreground"> · </span>
            {quote.title}
          </>
        ) : quoteQuery.isLoading ? (
          <Skeleton className="h-4 w-48" />
        ) : (
          'Current quote'
        )}
      </span>
      {quote ? (
        <div className="flex shrink-0 items-center gap-1 sm:gap-2">
          {workflow ? (
            <span className="hidden text-xs font-medium text-muted-foreground xl:inline">
              {workflow.stages.filter((stage) => stage.status === 'complete').length} of {workflow.stages.length}{' '}
              complete
            </span>
          ) : null}
          <Button asChild size="sm" variant="ghost">
            <Link aria-label="Version history" params={{ quoteId }} to="/quotes/$quoteId/versions">
              <History aria-hidden="true" />
              <span className="hidden xl:inline">History</span>
            </Link>
          </Button>
          <Button asChild size="sm" variant="ghost">
            <Link
              aria-label={actions ? `Customer emails, ${actions} to do` : 'Customer emails'}
              params={{ quoteId }}
              to="/quotes/$quoteId/emails"
            >
              <Mail aria-hidden="true" />
              <span className="hidden xl:inline">Emails</span>
              {actions ? (
                <span className="grid min-w-5 place-items-center rounded-full bg-red-600 px-1.5 text-[11px] font-semibold leading-5 text-white tabular-nums">
                  {actions}
                </span>
              ) : null}
            </Link>
          </Button>
          <Button asChild size="sm" variant="ghost">
            <Link
              aria-label={
                unanswered
                  ? `Customer comments, ${unanswered} ${unanswered === 1 ? 'needs' : 'need'} an answer`
                  : 'Customer comments'
              }
              params={{ quoteId }}
              to="/quotes/$quoteId/comments"
            >
              <MessageSquareText aria-hidden="true" />
              <span className="hidden xl:inline">Comments</span>
              {unanswered ? (
                <span className="grid min-w-5 place-items-center rounded-full bg-red-600 px-1.5 text-[11px] font-semibold leading-5 text-white tabular-nums">
                  {unanswered}
                </span>
              ) : null}
            </Link>
          </Button>
          <Button asChild size="sm" variant="ghost">
            <Link aria-label="Customer share" params={{ quoteId }} to="/quotes/$quoteId/share">
              <Share2 aria-hidden="true" />
              <span className="hidden xl:inline">Customer share</span>
            </Link>
          </Button>
          <PublishQuoteAction quote={quote} />
          <ProjectLinkAction quote={quote} />
        </div>
      ) : null}
    </div>
  )
}
